import React, { useState, useEffect } from "react";
import "./2DemoNewCase.css";

const DemoNewCase = () => {
  // 기본 정보 상태 관리
  const [basicInfo, setBasicInfo] = useState({
    pt_name: "",
    pt_number: "",
    birth: "",
    age: "",
    sex: "",
    admissionDate: "",
    height: "",
    weight: "",
    waist_circumference: "",
    hip_circumference: "",
    ED_arrival_time: "",
    transfer: "",
    mainComplaint: "",
    pastHistory: [],
    medicationHistory: "",
    allergies: [],
    isFastTrack: false,
    assignedDoctor: "",
    priority: "normal",
    notes: "",
  });

  // 데이터 컬럼 설정 상태 관리
  const [columnSettings, setColumnSettings] = useState({
    gcgX: "GCG_X",
    gcgY: "GCG_Y",
    gcgZ: "GCG_Z",
    ecg: "ECG_RAW",
    samplingRate: 200,
  });

  // 샘플 데이터 상태
  const [sampleData, setSampleData] = useState(null);

  // 샘플 데이터 생성 함수
  const generateSampleData = () => {
    const samplingRate = 200; // Hz
    const duration = 10; // 10초
    const samples = samplingRate * duration;
    const timeStep = 1 / samplingRate;

    // GCG X축 데이터 (심장의 좌우 회전 움직임)
    const gcgXData = Array.from({ length: samples }, (_, i) => {
      const t = i * timeStep;
      const heartRate = 70; // bpm
      const frequency = heartRate / 60; // Hz

      return {
        time: t,
        value:
          0.3 * Math.sin(2 * Math.PI * frequency * t) +
          0.1 * Math.sin(2 * Math.PI * frequency * 2 * t) +
          0.05 * (Math.random() - 0.5),
      };
    });

    // GCG Y축 데이터 (심장의 앞뒤 회전 움직임)
    const gcgYData = Array.from({ length: samples }, (_, i) => {
      const t = i * timeStep;
      const heartRate = 70;
      const frequency = heartRate / 60;

      return {
        time: t,
        value:
          0.25 * Math.cos(2 * Math.PI * frequency * t) +
          0.08 * Math.cos(2 * Math.PI * frequency * 1.5 * t) +
          0.04 * (Math.random() - 0.5),
      };
    });

    // GCG Z축 데이터 (심장의 상하 회전 움직임)
    const gcgZData = Array.from({ length: samples }, (_, i) => {
      const t = i * timeStep;
      const heartRate = 70;
      const frequency = heartRate / 60;

      return {
        time: t,
        value:
          0.2 * Math.sin(2 * Math.PI * frequency * t + Math.PI / 4) +
          0.06 * Math.sin(2 * Math.PI * frequency * 3 * t) +
          0.03 * (Math.random() - 0.5),
      };
    });

    // ECG 데이터 (심전도 파형)
    const ecgData = Array.from({ length: samples }, (_, i) => {
      const t = i * timeStep;
      const heartRate = 70;
      const rr_interval = 60 / heartRate;
      const phase = ((t % rr_interval) / rr_interval) * 2 * Math.PI;

      let ecgValue = 0;

      if (phase >= 0 && phase <= 0.4) {
        ecgValue += 0.1 * Math.sin(phase * 2.5);
      }

      if (phase >= 1.2 && phase <= 1.8) {
        const qrsPhase = (phase - 1.2) / 0.6;
        if (qrsPhase <= 0.3) {
          ecgValue -= 0.2 * Math.sin((qrsPhase * Math.PI) / 0.3);
        } else if (qrsPhase <= 0.7) {
          ecgValue += 1.0 * Math.sin(((qrsPhase - 0.3) * Math.PI) / 0.4);
        } else {
          ecgValue -= 0.3 * Math.sin(((qrsPhase - 0.7) * Math.PI) / 0.3);
        }
      }

      if (phase >= 2.5 && phase <= 3.5) {
        ecgValue += 0.3 * Math.sin((phase - 2.5) * Math.PI);
      }

      ecgValue += 0.02 * (Math.random() - 0.5);

      return {
        time: t,
        value: ecgValue,
      };
    });

    return {
      gcgX: gcgXData,
      gcgY: gcgYData,
      gcgZ: gcgZData,
      ecg: ecgData,
    };
  };

  // 컴포넌트 마운트 시 샘플 데이터 생성
  useEffect(() => {
    setSampleData(generateSampleData());
  }, []);

  // SVG 경로 생성 함수 수정
  const generatePath = (data, width, height) => {
    if (!data || data.length === 0) return "";

    const minX = 0;
    const maxX = 10; // 10초
    const minY = -1.5;
    const maxY = 1.5;

    // 데이터 포인트를 SVG 좌표로 변환
    const scaleX = width / (maxX - minX);
    const scaleY = height / (maxY - minY);

    return data
      .map((point, i) => {
        const x = (point.time - minX) * scaleX;
        const y = height - (point.value - minY) * scaleY;
        return `${i === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  };

  // 그래프 컴포넌트
  const TimeSeriesGraph = ({ data, color, title, type }) => {
    // 컨테이너 크기에 맞춰 SVG 크기 조정
    const containerWidth = type === "ecg" ? 1200 : 800;
    const containerHeight = type === "ecg" ? 300 : 180;

    // 실제 그래프가 그려질 영역 크기
    const graphWidth = containerWidth - 60; // 좌우 패딩 30px씩
    const graphHeight = containerHeight - 60; // 상하 패딩 30px씩

    // 데이터 스케일링을 위한 범위 설정
    const padding = {
      top: 30,
      right: 30,
      bottom: 30,
      left: 30,
    };

    // SVG 뷰포트 설정
    const viewBox = `0 0 ${containerWidth} ${containerHeight}`;

    return (
      <div className="chart-wrapper">
        <h3>{title}</h3>
        <div className="chart-container">
          <svg
            width="100%"
            height="10%"
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            className="time-series-graph"
          >
            {/* 배경 그리드 */}
            <g transform={`translate(${padding.left}, ${padding.top})`}>
              {/* X축 */}
              <line
                x1="0"
                y1={graphHeight}
                x2={graphWidth}
                y2={graphHeight}
                stroke="#ccc"
                strokeWidth="1"
              />
              {/* Y축 */}
              <line
                x1="0"
                y1="0"
                x2="0"
                y2={graphHeight}
                stroke="#ccc"
                strokeWidth="1"
              />
              {/* X축 눈금 */}
              {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
                <g
                  key={tick}
                  transform={`translate(${graphWidth * tick}, ${graphHeight})`}
                >
                  <line y2="5" stroke="#ccc" />
                  <text y="20" textAnchor="middle" fill="#666" fontSize="12">
                    {(tick * 10).toFixed(1)}s
                  </text>
                </g>
              ))}
              {/* Y축 눈금 */}
              {[-1, -0.5, 0, 0.5, 1].map((tick) => (
                <g
                  key={tick}
                  transform={`translate(0, ${
                    graphHeight * (1 - (tick + 1) / 2)
                  })`}
                >
                  <line x2="-5" stroke="#ccc" />
                  <text
                    x="-10"
                    y="5"
                    textAnchor="end"
                    fill="#666"
                    fontSize="12"
                  >
                    {tick.toFixed(1)}
                  </text>
                </g>
              ))}
              {/* 데이터 경로 */}
              <path
                d={generatePath(data, graphWidth, graphHeight)}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
              />
            </g>
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="vip-11-container">
      <div className="vip-content">
        <div className="vip-form-container">
          {/* 기본 정보 섹션 */}
          <div className="vip-form-section">
            <h2>기본 정보</h2>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">1-1. 기본정보</h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">환자이름</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="pt_name"
                      name="pt_name"
                      value={basicInfo.pt_name}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, pt_name: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">환자번호</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="pt_number"
                      name="pt_number"
                      value={basicInfo.pt_number}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          pt_number: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">생년월일</label>
                  <div className="input_2col">
                    <input
                      type="date"
                      id="birth"
                      name="birth"
                      value={basicInfo.birth}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, birth: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">나이</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="age"
                      name="age"
                      value={basicInfo.age}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, age: e.target.value })
                      }
                      readOnly={!!basicInfo.birth}
                    />
                    <span className="unit-text">세</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">성별</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="sex"
                        value="male"
                        checked={basicInfo.sex === "male"}
                        onChange={(e) =>
                          setBasicInfo({ ...basicInfo, sex: e.target.value })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">남성</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="sex"
                        value="female"
                        checked={basicInfo.sex === "female"}
                        onChange={(e) =>
                          setBasicInfo({ ...basicInfo, sex: e.target.value })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">여성</div>
                    </label>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">입원일</label>
                  <div className="input_2col">
                    <input
                      type="date"
                      id="admissionDate"
                      name="admissionDate"
                      value={basicInfo.admissionDate}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          admissionDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">키</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="height"
                      name="height"
                      value={basicInfo.height}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, height: e.target.value })
                      }
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">몸무게</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="weight"
                      name="weight"
                      value={basicInfo.weight}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, weight: e.target.value })
                      }
                    />
                    <span className="unit-text">kg</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">허리 둘레</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="waist_circumference"
                      name="waist_circumference"
                      value={basicInfo.waist_circumference}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          waist_circumference: e.target.value,
                        })
                      }
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">엉덩이 둘레</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="hip_circumference"
                      name="hip_circumference"
                      value={basicInfo.hip_circumference}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          hip_circumference: e.target.value,
                        })
                      }
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">응급실 도착시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_arrival_time"
                      name="ED_arrival_time"
                      max="9999-12-31T23:59"
                      value={basicInfo.ED_arrival_time}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          ED_arrival_time: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">타 병원에서 전원 옴</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="transfer"
                        value="yes"
                        checked={basicInfo.transfer === "yes"}
                        onChange={(e) =>
                          setBasicInfo({
                            ...basicInfo,
                            transfer: e.target.value,
                          })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="transfer"
                        value="no"
                        checked={basicInfo.transfer === "no"}
                        onChange={(e) =>
                          setBasicInfo({
                            ...basicInfo,
                            transfer: e.target.value,
                          })
                        }
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">주호소</label>
                  <div className="input_1col">
                    <input
                      type="text"
                      id="mainComplaint"
                      name="mainComplaint"
                      value={basicInfo.mainComplaint}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          mainComplaint: e.target.value,
                        })
                      }
                      placeholder="주요 증상이나 호소 사항을 입력하세요"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">과거력</label>
                  <div className="input_1col">
                    <div className="checkbox-group">
                      {["고혈압", "당뇨", "심장질환", "뇌졸중", "암"].map(
                        (option) => (
                          <label key={option} className="checkbox-label">
                            <input
                              type="checkbox"
                              value={option}
                              checked={basicInfo.pastHistory.includes(option)}
                              onChange={(e) => {
                                const value = e.target.value;
                                const isChecked = e.target.checked;
                                setBasicInfo((prev) => ({
                                  ...prev,
                                  pastHistory: isChecked
                                    ? [...prev.pastHistory, value]
                                    : prev.pastHistory.filter(
                                        (item) => item !== value
                                      ),
                                }));
                              }}
                            />
                            {option}
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">복용 중인 약물</label>
                  <div className="input_1col">
                    <textarea
                      id="medicationHistory"
                      name="medicationHistory"
                      value={basicInfo.medicationHistory}
                      onChange={(e) =>
                        setBasicInfo({
                          ...basicInfo,
                          medicationHistory: e.target.value,
                        })
                      }
                      placeholder="현재 복용 중인 약물을 입력하세요"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">알레르기</label>
                  <div className="input_1col">
                    <div className="checkbox-group">
                      {["페니실린", "설파제", "조영제", "없음"].map(
                        (option) => (
                          <label key={option} className="checkbox-label">
                            <input
                              type="checkbox"
                              value={option}
                              checked={basicInfo.allergies.includes(option)}
                              onChange={(e) => {
                                const value = e.target.value;
                                const isChecked = e.target.checked;
                                setBasicInfo((prev) => ({
                                  ...prev,
                                  allergies: isChecked
                                    ? [...prev.allergies, value]
                                    : prev.allergies.filter(
                                        (item) => item !== value
                                      ),
                                }));
                              }}
                            />
                            {option}
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">메모</label>
                  <div className="input_1col">
                    <textarea
                      id="notes"
                      name="notes"
                      value={basicInfo.notes}
                      onChange={(e) =>
                        setBasicInfo({ ...basicInfo, notes: e.target.value })
                      }
                      placeholder="추가 메모사항을 입력하세요"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 시계열 데이터 업로드 섹션 */}
          <div className="vip-form-section">
            <h2>시계열 데이터 업로드</h2>
            <div className="file-upload-container">
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">CSV 파일</label>
                  <div className="input_1col">
                    <div className="file-upload-box">
                      <div className="file-upload-status">
                        <div className="file-info">
                          <span className="file-name">sample_data.csv</span>
                          <span className="file-size">(2.5MB)</span>
                        </div>
                        <button type="button" className="file-change-button">
                          파일 변경
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="section-subtitle">데이터 컬럼 설정</div>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">GCG X축 컬럼명</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.gcgX}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          gcgX: e.target.value,
                        })
                      }
                      placeholder="예: GCG_X"
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">GCG Y축 컬럼명</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.gcgY}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          gcgY: e.target.value,
                        })
                      }
                      placeholder="예: GCG_Y"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">GCG Z축 컬럼명</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.gcgZ}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          gcgZ: e.target.value,
                        })
                      }
                      placeholder="예: GCG_Z"
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">ECG 데이터 컬럼명</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      value={columnSettings.ecg}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          ecg: e.target.value,
                        })
                      }
                      placeholder="예: ECG_RAW"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">샘플링 주파수 (Hz)</label>
                  <div className="input_2col">
                    <input
                      type="number"
                      value={columnSettings.samplingRate}
                      onChange={(e) =>
                        setColumnSettings({
                          ...columnSettings,
                          samplingRate: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="예: 200"
                    />
                  </div>
                </div>
                <div className="block-2column"></div>
              </div>
            </div>
          </div>

          {/* 데이터 시각화 섹션 */}
          <div className="data-visualization-section">
            <h2>Data Compile</h2>
            <div className="charts-container">
              {sampleData && (
                <>
                  <div className="gcg-charts-section">
                    <TimeSeriesGraph
                      data={sampleData.gcgX}
                      color="#2196F3"
                      title="GCG - X Axis"
                      type="gcg"
                    />
                    <TimeSeriesGraph
                      data={sampleData.gcgY}
                      color="#4CAF50"
                      title="GCG - Y Axis"
                      type="gcg"
                    />
                    <TimeSeriesGraph
                      data={sampleData.gcgZ}
                      color="#FF9800"
                      title="GCG - Z Axis"
                      type="gcg"
                    />
                  </div>
                  <div className="ecg-chart-section">
                    <TimeSeriesGraph
                      data={sampleData.ecg}
                      color="#E91E63"
                      title="ECG (Peak)"
                      type="ecg"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 저장 버튼 */}
          <div className="save-button-container">
            <button className="vip-submit-button large">저장하기</button>
          </div>

          {/* 통계 대시보드 섹션 */}
          <div className="vip-form-section statistics-dashboard">
            <h2>통계 대시보드</h2>

            <div className="statistics-summary">
              <div className="stat-card">
                <h3>총 데이터 포인트</h3>
                <div className="stat-value">12,458</div>
                <div className="stat-trend positive">
                  <span>+3.2%</span> 이전 측정 대비
                </div>
              </div>

              <div className="stat-card">
                <h3>평균 심박수</h3>
                <div className="stat-value">72 BPM</div>
                <div className="stat-trend neutral">
                  <span>±0.5</span> 정상 범위
                </div>
              </div>

              <div className="stat-card">
                <h3>GCG 신호 품질</h3>
                <div className="stat-value">98.5%</div>
                <div className="stat-trend positive">
                  <span>+1.2%</span> 이전 측정 대비
                </div>
              </div>

              <div className="stat-card">
                <h3>ECG 신호 품질</h3>
                <div className="stat-value">99.2%</div>
                <div className="stat-trend positive">
                  <span>+0.8%</span> 이전 측정 대비
                </div>
              </div>
            </div>

            <div className="statistics-grid">
              <div className="chart-container">
                <div className="chart-header">
                  <h3>GCG 진폭 분포</h3>
                </div>
                <div className="bar-chart">
                  <div className="chart-bars">
                    {[0.2, 0.5, 0.8, 1.0, 0.7, 0.4].map((value, index) => (
                      <div
                        key={index}
                        className="chart-bar"
                        style={{ height: `${value * 200}px` }}
                      >
                        <span>{Math.round(value * 100)}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="chart-labels">
                    {["X-min", "X-max", "Y-min", "Y-max", "Z-min", "Z-max"].map(
                      (label, index) => (
                        <span key={index}>{label}</span>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <h3>ECG 피크 분포</h3>
                </div>
                <div className="line-chart">
                  <svg className="line-svg" viewBox="0 0 800 240">
                    {/* 격자 */}
                    {[...Array(5)].map((_, i) => (
                      <line
                        key={`grid-h-${i}`}
                        x1="40"
                        y1={30 + i * 45}
                        x2="760"
                        y2={30 + i * 45}
                        stroke="#f1f5f9"
                        strokeWidth="1"
                      />
                    ))}

                    {/* Y축 */}
                    <line
                      x1="40"
                      y1="30"
                      x2="40"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="1"
                    />

                    {/* X축 */}
                    <line
                      x1="40"
                      y1="210"
                      x2="760"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="1"
                    />

                    {/* 데이터 라인 */}
                    <path
                      d="M 40 120 C 200 80, 400 160, 760 100"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                    />

                    {/* 데이터 포인트 */}
                    {[
                      { x: 40, y: 120 },
                      { x: 200, y: 80 },
                      { x: 400, y: 160 },
                      { x: 760, y: 100 },
                    ].map((point, index) => (
                      <circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r="4"
                        fill="#3b82f6"
                        stroke="white"
                        strokeWidth="2"
                      />
                    ))}
                  </svg>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <h3>신호 품질 분포</h3>
                </div>
                <div className="pie-chart">
                  <svg width="180" height="180" viewBox="0 0 180 180">
                    <path
                      d="M 90 90 L 170 90 A 80 80 0 0 1 90 170 Z"
                      fill="#3b82f6"
                      stroke="white"
                    />
                    <path
                      d="M 90 90 L 90 170 A 80 80 0 0 1 10 90 Z"
                      fill="#10b981"
                      stroke="white"
                    />
                    <path
                      d="M 90 90 L 10 90 A 80 80 0 0 1 90 10 Z"
                      fill="#f59e0b"
                      stroke="white"
                    />
                    <path
                      d="M 90 90 L 90 10 A 80 80 0 0 1 170 90 Z"
                      fill="#ef4444"
                      stroke="white"
                    />
                  </svg>
                  <div className="pie-legend">
                    <div className="legend-item">
                      <div
                        className="color-dot"
                        style={{ backgroundColor: "#3b82f6" }}
                      ></div>
                      <span>우수 (45%)</span>
                    </div>
                    <div className="legend-item">
                      <div
                        className="color-dot"
                        style={{ backgroundColor: "#10b981" }}
                      ></div>
                      <span>양호 (30%)</span>
                    </div>
                    <div className="legend-item">
                      <div
                        className="color-dot"
                        style={{ backgroundColor: "#f59e0b" }}
                      ></div>
                      <span>보통 (15%)</span>
                    </div>
                    <div className="legend-item">
                      <div
                        className="color-dot"
                        style={{ backgroundColor: "#ef4444" }}
                      ></div>
                      <span>미흡 (10%)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <h3>시간대별 측정 분포</h3>
                </div>
                <div className="scatter-plot">
                  <svg className="scatter-svg" viewBox="0 0 800 240">
                    {/* 격자 */}
                    {[...Array(5)].map((_, i) => (
                      <line
                        key={`grid-h-${i}`}
                        x1="60"
                        y1={30 + i * 45}
                        x2="740"
                        y2={30 + i * 45}
                        stroke="#f1f5f9"
                        strokeWidth="1"
                      />
                    ))}

                    {/* Y축 */}
                    <line
                      x1="60"
                      y1="30"
                      x2="60"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="1"
                    />

                    {/* X축 */}
                    <line
                      x1="60"
                      y1="210"
                      x2="740"
                      y2="210"
                      stroke="#cbd5e1"
                      strokeWidth="1"
                    />

                    {/* 데이터 포인트 */}
                    {Array.from({ length: 20 }, (_, i) => ({
                      x: 60 + Math.random() * 680,
                      y: 30 + Math.random() * 180,
                      type: Math.random() > 0.5 ? "gcg" : "ecg",
                    })).map((point, index) => (
                      <circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r="5"
                        fill={point.type === "gcg" ? "#3b82f6" : "#ef4444"}
                        opacity="0.7"
                        stroke="white"
                        strokeWidth="1"
                      />
                    ))}

                    {/* 범례 */}
                    <circle cx="660" cy="20" r="4" fill="#3b82f6" />
                    <text x="670" y="23" fontSize="10" fill="#64748b">
                      GCG
                    </text>
                    <circle cx="700" cy="20" r="4" fill="#ef4444" />
                    <text x="710" y="23" fontSize="10" fill="#64748b">
                      ECG
                    </text>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoNewCase;
